import { useEffect, useRef, useState } from 'react';

interface DemoPlayerProps {
  title: string;
  poster: string;
}

const TOTAL_SECONDS = 92 * 60 + 18;

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function DemoPlayer({ title, poster }: DemoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= TOTAL_SECONDS) {
          setPlaying(false);
          return TOTAL_SECONDS;
        }
        return prev + 3;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing]);

  const percent = (progress / TOTAL_SECONDS) * 100;

  const toggleFullscreen = () => {
    const element = wrapRef.current;
    if (!element) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else {
      element.requestFullscreen().catch(() => undefined);
    }
  };

  return (
    <div>
      <div
        ref={wrapRef}
        className="group relative aspect-video w-full overflow-hidden rounded-lg bg-primary-950"
      >
        <img
          src={poster}
          alt={`${title} 播放入口画面`}
          title={`${title} - 播放演示 - 黄牛视频`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-primary-950/55" />

        {!playing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-4 text-center">
            <button
              type="button"
              aria-label={`播放${title}演示`}
              onClick={() => setPlaying(true)}
              className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-accent-950 transition-transform hover:scale-105 md:h-20 md:w-20"
            >
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </button>
            <p className="text-sm font-medium text-background-50">{title} · 播放演示</p>
            <p className="max-w-md text-[11px] leading-relaxed text-background-100/70">
              当前为前端播放控件演示，不包含真实视频源，所有进度与画质均为模拟效果。
            </p>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-transparent px-3 pb-2.5 pt-8 opacity-100 md:px-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded-full bg-accent-500/90 px-2 py-0.5 font-label text-[9px] font-bold uppercase tracking-wider text-accent-950">
              演示模式
            </span>
            <span className="text-[10px] text-background-100/70">
              非真实播放源 · 视频能力待接入
            </span>
          </div>

          <input
            type="range"
            min={0}
            max={TOTAL_SECONDS}
            value={progress}
            aria-label="播放进度"
            onChange={(event) => setProgress(Number(event.target.value))}
            className="player-range w-full cursor-pointer"
            style={{
              background: `linear-gradient(to right, oklch(var(--accent-500)) 0%, oklch(var(--accent-500)) ${percent}%, oklch(var(--background-50) / 0.28) ${percent}%, oklch(var(--background-50) / 0.28) 100%)`,
            }}
          />

          <div className="mt-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 md:gap-2">
              <button
                type="button"
                aria-label={playing ? '暂停' : '播放'}
                onClick={() => setPlaying((prev) => !prev)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`} />
              </button>
              <button
                type="button"
                aria-label="后退十秒"
                onClick={() => setProgress((prev) => Math.max(0, prev - 10))}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-replay-10-line text-base" />
              </button>
              <button
                type="button"
                aria-label={muted ? '取消静音' : '静音'}
                onClick={() => setMuted((prev) => !prev)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className={`${muted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-base`} />
              </button>
              <span className="font-label text-[11px] text-background-100/85">
                {formatTime(progress)} / {formatTime(TOTAL_SECONDS)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="hidden rounded border border-background-50/30 px-1.5 py-0.5 text-[10px] text-background-100/80 sm:inline">
                自动 1080P
              </span>
              <button
                type="button"
                aria-label="全屏"
                onClick={toggleFullscreen}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-fullscreen-line text-base" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary-50 px-3 py-2.5">
        <p className="flex items-center gap-1.5 text-[11px] leading-relaxed text-secondary-900">
          <i className="ri-information-line text-sm" />
          演示说明：播放、进度拖动、静音与全屏均为前端交互演示，未接入真实视频数据接口。
        </p>
        <span className="font-label text-[10px] uppercase tracking-wider text-secondary-700">
          Demo only
        </span>
      </div>
    </div>
  );
}